import React, {Fragment, useState, useEffect} from 'react';
import {Select, Input, InputNumber, Row, Col, Form, Typography, Tag, Checkbox, Space} from 'antd';
import {remove, unset, isUndefined} from 'lodash';
import {MarkdownEditor} from './markdown-editor';

// Kept in sync with the colors FieldStateEditor uses for read-mode tags
// so toggling a flag here and the resulting tag in the State column stay
// visually aligned.
const FLAG_COLORS = {
    required: 'green',
    'write-only': 'magenta',
    'read-only': 'gold',
    nullable: 'cyan',
    deprecated: 'red',
};

export function FieldNameAndDescriptionEditor({record, onSave, contextPath}) {
    const [form] = Form.useForm();
    const [validationName, setValidationName] = useState({});
    const [validationMinLength, setValidationMinLength] = useState({});
    const [validationMaxLength, setValidationMaxLength] = useState({});
    const [newEnum, setNewEnum] = useState();

    useEffect(() => {
        if (record) {
            if (record.externalDocs) {
                form.setFieldsValue({externalDocsUrl: record.externalDocs.url, externalDocsDescription: record.externalDocs.description});
            }
        }
    }, [record]);

    const onEdit = (changed) => {
        if (changed.minLength) {
            setValidationMinLength(validate(changed));
        } else if (changed.maxLength) {
            setValidationMaxLength(validate(changed));
        } else {
            setValidationName(validate(changed));
        }
        // Name renames are owned by the Input's onBlur handler. Firing
        // onEditName from onValuesChange too would dispatch a rename per
        // keystroke, which causes the parent schema to re-render and the
        // whole edit form to unmount/remount mid-type — the flicker the
        // user reported. Early-return when only `name` changed and let
        // blur commit the final value.
        if (changed.name !== undefined && Object.keys(changed).length === 1) {
            return;
        }
        if (!changed.name) {
            changed.propName = contextPath ? `${contextPath}.${record.name}` : `properties.${record.name}`;

            if (changed.type === 'object') {
                changed.properties = record.properties || {};
            } else if (changed.type === 'array') {
                changed.items = record.items || {};
            } else if (['object', 'array'].indexOf(record.type) === -1) {
                unset(changed, 'properties');
                unset(changed, 'items');
            }

            if (changed.itemType) {
                changed.items = record.items || {};
                changed.items.type = changed.itemType;
                unset(changed, 'itemType');
                if (changed.items.type === 'string' || changed.items.type === 'number') {
                    unset(changed, 'items.properties');
                    unset(changed, 'items.items');
                } else if (changed.items.type === 'array') {
                    changed.items.items = {type: 'object', properties: {}};
                } else if (changed.items.type === 'object') {
                    changed.items.properties = {};
                }
            }

            if (!isUndefined(changed.externalDocsUrl) || !isUndefined(changed.externalDocsDescription)) {
                changed.externalDocs = {
                    url: changed.externalDocsUrl,
                    description: changed.externalDocsDescription,
                };
                unset(changed, 'externalDocsDescription');
                unset(changed, 'externalDocsUrl');
            }

            onSave(changed);
        } else {
            onEditName({key: 'name', name: changed.name});
        }
    };

    function validate(changedValue) {
        if (changedValue.minLength && parseInt(changedValue.minLength) < 0) {
            return {
                validateStatus: 'error',
                errorMsg: 'The minLength is not to be negative value',
            };
        } else if (changedValue.maxLength && !(/^[0-9]+$/.test(changedValue.maxLength) || changedValue.maxLength === '*')) {
            return {
                validateStatus: 'error',
                errorMsg: 'The maxLength should be number or * format',
            };
        } else if (changedValue.name === '') {
            return {
                validateStatus: 'error',
                errorMsg: 'Name cannot be blank',
            };
        } else if (changedValue.name && changedValue.name.indexOf(' ') > 0) {
            return {
                validateStatus: 'error',
                errorMsg: 'Name cannot contain white space',
            };
        }
        if (changedValue.name && !changedValue.name.match(/^[a-zA-Z0-9\-_]+$/)) {
            return {
                validateStatus: 'error',
                errorMsg: 'Name must be a valid identifier (no space, no dot, starts with letter)',
            };
        } else {
            return {
                validateStatus: 'success',
                errorMsg: null,
            };
        }
    }

    const onEditName = (changed) => {
        changed.propName = contextPath ? `${contextPath}.${changed.name}` : `properties.${changed.name}`;
        changed.key = contextPath ? `${contextPath}.${record.name}` : `properties.${record.name}`;
        onSave(changed);
    };

    function confirmNewEnum(e) {
        e.preventDefault();
        if (newEnum) {
            record.enum.push(newEnum);
            setNewEnum('');

            const changed = {
                propName: contextPath ? `${contextPath}.${record.name}` : `properties.${record.name}`,
                key: contextPath ? `${contextPath}.${record.name}` : `properties.${record.name}`,
                enum: record.enum,
            };
            onSave(changed);
        }
        return false;
    }

    function removeEnumValue(val) {
        return function (e) {
            e.preventDefault();
            remove(record.enum, (v) => v == val);
            const changed = {
                propName: contextPath ? `${contextPath}.${record.name}` : `properties.${record.name}`,
                key: contextPath ? `${contextPath}.${record.name}` : `properties.${record.name}`,
                enum: record.enum,
            };
            onSave(changed);
        };
    }

    const hasFormat = () => ['string', 'number'].indexOf(record.type) !== -1;
    const isArray = () => record.type === 'array';

    // State-flag checkbox logic lifted from FieldStateEditor so all
    // field-level editing happens in this single form (the State column
    // now just shows read-mode tags, giving the editor more horizontal
    // room). Required is special: it updates the parent object's
    // `required` array via the `set-required` op; other flags write a
    // boolean directly onto the field.
    const stateHas = (key) => !!(record.state || []).find((s) => s.text === key);
    const toggleFlag = (flag, field) => (e) => {
        const checked = e.target.checked;
        record.state = record.state || [];
        if (!checked) {
            remove(record.state, (s) => s.text === flag);
        } else {
            record.state.push({text: flag, color: FLAG_COLORS[flag]});
        }

        // Read the current name from the form, not from the (possibly
        // stale) record prop: when the user edits the Name field and
        // then clicks a flag checkbox in one motion, Input.onBlur fires
        // first and queues a rename, but toggleFlag's closure was
        // captured at the pre-rename render so record.name is still the
        // old key. Using the form value here aligns the flag dispatch
        // with the renamed property.
        const currentName = form.getFieldValue('name') || record.name;
        const changeSet = {key: currentName, state: record.state};

        if (flag === 'required') {
            changeSet.op = 'set-required';
            if (contextPath) {
                const pathElements = contextPath.split('.');
                pathElements.pop();
                pathElements.push('required');
                changeSet.propName = pathElements.join('.');
            } else {
                changeSet.propName = 'required';
            }
            changeSet.required = checked;
        } else {
            changeSet.propName = contextPath ? `${contextPath}.${currentName}` : `properties.${currentName}`;
            changeSet[field || flag] = checked;
        }

        onSave(changeSet);
    };

    return (
        <Form form={form} initialValues={record} onValuesChange={onEdit} size="small" layout="vertical" className="fieldEditor">
            <Row>
                <Col xs={24}>
                    <Form.Item label="Name" name="name" validateStatus={validationName.validateStatus} help={validationName.errorMsg}>
                        <Input placeholder="name" onBlur={(e) => onEditName({key: record.name, name: e.target.value})} />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={hasFormat() || isArray() ? 12 : 24}>
                    <Form.Item label="Type" name="type">
                        <Select style={{width: '100%'}}>
                            <Select.Option value="string">string</Select.Option>
                            <Select.Option value="number">number</Select.Option>
                            <Select.Option value="boolean">boolean</Select.Option>
                            <Select.Option value="object">object</Select.Option>
                            <Select.Option value="array">array</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                {hasFormat() && (
                    <Col xs={11} offset={1}>
                        <Form.Item label="Format" name="format">
                            <Select style={{width: '100%'}}>
                                <Select.Option value="">(unspecified)</Select.Option>
                                {record.type === 'number' && <Select.Option value="int32">integer</Select.Option>}
                                {record.type === 'number' && <Select.Option value="int64">long</Select.Option>}
                                {record.type === 'number' && <Select.Option value="float">float</Select.Option>}
                                {record.type === 'number' && <Select.Option value="double">double</Select.Option>}
                                {record.type === 'string' && <Select.Option value="base64">base64</Select.Option>}
                                {record.type === 'string' && <Select.Option value="binary">binary</Select.Option>}
                                {record.type === 'string' && <Select.Option value="date">date</Select.Option>}
                                {record.type === 'string' && <Select.Option value="date-time">date-time</Select.Option>}
                                {record.type === 'string' && <Select.Option value="password">password</Select.Option>}
                            </Select>
                        </Form.Item>
                    </Col>
                )}
                {isArray() && (
                    <Col xs={11} offset={1}>
                        <Form.Item label="Items Type" name="itemType">
                            <Select style={{width: '100%'}}>
                                <Select.Option value="string">string</Select.Option>
                                <Select.Option value="number">number</Select.Option>
                                <Select.Option value="object">object</Select.Option>
                                <Select.Option value="array">array</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                )}
            </Row>
            <Row>
                <Col xs={24}>
                    <Form.Item label="Description" name="description">
                        <MarkdownEditor height={200} />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <Form.Item label="Flags" className="fieldEditor__flags">
                        <Space wrap>
                            <Checkbox checked={stateHas('required')} onChange={toggleFlag('required')}>
                                Required
                            </Checkbox>
                            <Checkbox checked={stateHas('read-only')} onChange={toggleFlag('read-only', 'readOnly')}>
                                Read-Only
                            </Checkbox>
                            <Checkbox checked={stateHas('write-only')} onChange={toggleFlag('write-only', 'writeOnly')}>
                                Write-Only
                            </Checkbox>
                            <Checkbox checked={stateHas('deprecated')} onChange={toggleFlag('deprecated')}>
                                Deprecated
                            </Checkbox>
                            <Checkbox checked={stateHas('nullable')} onChange={toggleFlag('nullable')}>
                                Nullable
                            </Checkbox>
                        </Space>
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <Form.Item label="Example" name="example">
                        <Input placeholder="simple example" />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <Form.Item
                        label="External Docs Url"
                        name="externalDocsUrl"
                        rules={[
                            {
                                type: 'url',
                                message: 'External Docs should be a URL',
                            },
                        ]}>
                        <Input type="url" placeholder="url to external doc" />
                    </Form.Item>
                    <Form.Item
                        label="External Docs Description"
                        name="externalDocsDescription"
                        rules={[
                            {
                                type: 'required',
                                message: 'External Docs description is required',
                            },
                        ]}>
                        <Input placeholder="external docs description" />
                    </Form.Item>
                </Col>
            </Row>
            {record.type === 'string' && (
                <Row>
                    <Col xs={18} className="enumGroup">
                        <Typography.Text>Enum Values:</Typography.Text>
                        <div style={{width: '100%'}}>
                            {(record.enum || []).map((v, idx) => (
                                <Tag style={{marginBottom: 10}} closable onClose={removeEnumValue(v)} key={idx}>
                                    {v}
                                </Tag>
                            ))}
                        </div>
                    </Col>
                    <Col xs={6}>
                        <Input placeholder="new enum value" value={newEnum} onChange={(e) => setNewEnum(e.target.value)} onBlur={confirmNewEnum} onPressEnter={confirmNewEnum} />
                    </Col>
                </Row>
            )}
            <Row>
                {record.type === 'string' && (
                    <Fragment>
                        <Col lg={8} md={8} xs={24}>
                            <Form.Item label="Min Length" name="minLength" validateStatus={validationMinLength.validateStatus} help={validationMinLength.errorMsg}>
                                <InputNumber />
                            </Form.Item>
                        </Col>
                        <Col lg={7} md={7} xs={24} offset={1}>
                            <Form.Item label="Max Length" name="maxLength" validateStatus={validationMaxLength.validateStatus} help={validationMaxLength.errorMsg}>
                                <InputNumber />
                            </Form.Item>
                        </Col>
                        <Col lg={7} md={7} xs={24} offset={1}>
                            <Form.Item
                                label="Pattern"
                                name="pattern"
                                rules={[
                                    {
                                        pattern: new RegExp('^[a-zA-Z0-9.-_]+$'),
                                        message: 'Wrong format!',
                                    },
                                ]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Fragment>
                )}
                {record.type === 'number' && (
                    <Fragment>
                        <Col xs={12}>
                            <Form.Item label="Min" name="minimum" validateStatus={validationMinLength.validateStatus} help={validationMinLength.errorMsg}>
                                <InputNumber />
                            </Form.Item>
                        </Col>
                        <Col xs={12}>
                            <Form.Item label="Max" name="maximum" validateStatus={validationMaxLength.validateStatus} help={validationMaxLength.errorMsg}>
                                <InputNumber />
                            </Form.Item>
                        </Col>
                    </Fragment>
                )}
            </Row>
        </Form>
    );
}
